import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Chip, IconButton, Tooltip, TextField, InputAdornment,
  Button, Dialog, DialogContent, DialogActions, DialogTitle,
  Divider, FormControl, InputLabel, Select, MenuItem, List,
  ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction, LinearProgress,
  Snackbar, Menu, Collapse
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TableChartIcon from '@mui/icons-material/TableChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ArticleIcon from '@mui/icons-material/Article';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GroupsIcon from '@mui/icons-material/Groups';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FolderIcon from '@mui/icons-material/Folder';
import api from '../../config/axiosConfig';
import PipcProgressDialog from '../servicios/PipcProgressDialog';
import { buildEnteStages, isPipComplete } from '../../utils/workflowSteps';
import { getExpirationStatus } from '../../utils/documentExpiration';

// ─── helpers ────────────────────────────────────────────────────────────────
// Los LocalDate del backend llegan como "YYYY-MM-DD" puro (sin hora). new Date("YYYY-MM-DD")
// lo interpreta como medianoche UTC, y toLocaleDateString lo vuelve a convertir a hora local --
// en México (UTC-6) eso recorre la fecha un día para atrás. Para fechas puras se arma la fecha
// en LOCAL (year, month, day) para que no haya conversión de zona horaria de por medio.
const fmtDate = (val) => {
  if (!val) return '—';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    const [y, m, d] = val.substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  const d = new Date(val);
  return isNaN(d) ? val : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtCurrency = (val) => {
  if (val == null) return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val);
};

const STATUS_LABEL = { PENDIENTE: 'Pendiente de Asignación', ASIGNADA: 'Asignada', PROGRAMADA: 'Programada', EN_PROGRESO: 'En Progreso', LISTA_PARA_REVISION: 'Lista para Revisión', COMPLETADA: 'Completada', CANCELADA: 'Cancelada' };
const STATUS_COLOR = { PENDIENTE: 'warning', ASIGNADA: 'secondary', PROGRAMADA: 'info', EN_PROGRESO: 'primary', LISTA_PARA_REVISION: 'secondary', COMPLETADA: 'success', CANCELADA: 'error' };
const CP_LABEL = { si: 'Sí', no: 'No', no_aplica: 'N/A' };

const SECTION_COLOR = {
  'Información General': '#1565c0',
  'Trámite PPC':         '#6a1b9a',
  'Seguimiento y Anuencia': '#2e7d32',
  'Entrega al Cliente':  '#e65100',
  'Finanzas':            '#1b5e20',
};

// Todos los campos del tablero con sección y cómo obtener el valor
const buildFields = (row, payments = []) => {
  const sumPagos = payments.reduce((acc, p) => acc + Number(p.amount || 0), 0);
  const saldo = row.totalAmount != null ? Number(row.totalAmount) - sumPagos : null;
  return [
  { section: 'Información General', label: 'Centro de Trabajo',                    value: row.companyName,          pending: false },
  { section: 'Información General', label: 'Orden de Trabajo',                      value: row.orderNumber,          pending: false },
  { section: 'Información General', label: 'Fecha programada de Inspección',        value: fmtDate(row.scheduledDate), pending: false },
  { section: 'Información General', label: 'Fecha programada de Capacitación',      value: row.trainingDate ? `${fmtDate(row.trainingDate)}${row.latestTrainingDate && row.latestTrainingDate !== row.earliestTrainingDate ? ` (hasta ${fmtDate(row.latestTrainingDate)})` : ''}` : null, pending: !row.trainingDate },
  { section: 'Información General', label: 'Fecha de recepción del Reporte',        value: null,                     pending: true  },
  { section: 'Información General', label: 'Link de reporte de inspección',         value: null,                     pending: true  },
  { section: 'Información General', label: 'Fecha de recepción de info completa',   value: null,                     pending: true  },
  { section: 'Trámite PPC',         label: 'Estatus PPC',                           value: STATUS_LABEL[row.status] || row.status, pending: false },
  { section: 'Trámite PPC',         label: 'Estatus del trámite ante PC',           value: null,                     pending: true  },
  { section: 'Trámite PPC',         label: 'Link del PPC',                          value: null,                     pending: true  },
  { section: 'Trámite PPC',         label: 'Fecha de ingreso PPC a PC',             value: null,                     pending: true  },
  { section: 'Trámite PPC',         label: 'Evidencia de ingreso PPC ante PC',      value: null,                     pending: true  },
  { section: 'Trámite PPC',         label: 'Estatus de pago de derechos ante PC',   value: null,                     pending: true  },
  { section: 'Seguimiento y Anuencia', label: 'Emisión de Factura del Municipio',   value: row.requiresInvoice != null ? (row.requiresInvoice ? 'Sí' : 'No') : null, pending: false },
  { section: 'Seguimiento y Anuencia', label: 'Estatus (Seguimiento)',              value: STATUS_LABEL[row.status] || row.status, pending: false },
  { section: 'Seguimiento y Anuencia', label: 'Fecha de liberación de Anuencia',   value: null,                     pending: true  },
  { section: 'Seguimiento y Anuencia', label: 'Vigencia de Anuencia',              value: null,                     pending: true  },
  { section: 'Seguimiento y Anuencia', label: 'Evidencia visto bueno PC',          value: CP_LABEL[row.civilProtectionApproval], pending: false },
  { section: 'Entrega al Cliente', label: 'Fecha entrega física final',            value: fmtDate(row.completedAt), pending: false },
  { section: 'Entrega al Cliente', label: 'Evidencia entrega al cliente',          value: null,                     pending: true  },
  { section: 'Entrega al Cliente', label: 'Encuesta de satisfacción',              value: null,                     pending: true  },
  // ── Finanzas ──
  { section: 'Finanzas', label: 'Total de la Orden',    value: fmtCurrency(row.totalAmount), pending: false },
  ...(payments.length === 0
    ? [{ section: 'Finanzas', label: 'Abonos registrados', value: 'Sin abonos', pending: false }]
    : payments.map((p, i) => ({
        section: 'Finanzas',
        label: `Abono ${i + 1}  ·  ${fmtDate(p.paymentDate)}`,
        value: `${fmtCurrency(p.amount)}  ·  ${p.paymentMethod || ''}${p.referenceNumber ? '  ·  Ref: ' + p.referenceNumber : ''}`,
        pending: false,
        highlight: true
      }))
  ),
  { section: 'Finanzas', label: 'Saldo Pendiente',      value: fmtCurrency(saldo),           pending: false, bold: saldo > 0 },
];};

// ─── main component ──────────────────────────────────────────────────────────
export default function TablaSeguimiento() {
  const navigate = useNavigate();
  const [rows, setRows]         = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected]         = useState(null);
  const [fieldSearch, setFieldSearch]   = useState('');
  const [payments, setPayments]         = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [pipcProgress, setPipcProgress] = useState({});
  // Mismos datos que usa el menú de Programas para el avance por etapas (ver buildEnteStages)
  const [summaryByQuotation, setSummaryByQuotation] = useState({});
  const [readinessByQuotation, setReadinessByQuotation] = useState({});
  const [flowByWorkOrder, setFlowByWorkOrder] = useState({});
  const [pipcDialogOpen, setPipcDialogOpen] = useState(false);
  const [pipcDialogRow, setPipcDialogRow]   = useState(null);
  const [generatingConstancias, setGeneratingConstancias] = useState(false);
  const [generatingPipc, setGeneratingPipc] = useState(new Set());
  const [pipcDialog, setPipcDialog] = useState({ open: false, loading: false, results: null, error: null, row: null, docType: 'PIPC', progressPct: 0 });
  const [constanciasDialog, setConstanciasDialog] = useState({ open: false, loading: false, results: null, error: null, row: null });
  const [confirmGenerateDialog, setConfirmGenerateDialog] = useState({ open: false, docType: null, row: null });
  // Para CONSTANCIAS, la confirmación usa generation-summary (mismo comportamiento que
  // Programas.jsx) en vez de la lista genérica de readiness que usan RIC/PIPC.
  const [constanciasSummary, setConstanciasSummary] = useState([]);
  const [constanciasSummaryLoading, setConstanciasSummaryLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [readinessMap, setReadinessMap] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  // Cuenta requests de datos secundarios en vuelo (readiness + progreso PIPC por fila) --
  // la tabla ya se ve pero esos badges/porcentajes todavía no son confiables mientras esto sea > 0
  const [pendingSecondary, setPendingSecondary] = useState(0);

  const toggleRowExpanded = (rowId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  };

  useEffect(() => { load(); }, []);

  // El backend cachea este endpoint ~45s, así que casi siempre responde rápido. Si aun así
  // falla, no reintentamos automático (evita disparar dos recálculos en paralelo) -- el botón
  // de refresh del header vuelve a llamar loadReadiness() cuando el usuario quiera.
  const loadReadiness = () => {
    setPendingSecondary(c => c + 1);
    api.get('/files/work-orders/readiness')
      .then(res => setReadinessMap(res.data || {}))
      .catch(() => {})
      .finally(() => setPendingSecondary(c => c - 1));
  };

  useEffect(() => { loadReadiness(); }, []);

  useEffect(() => {
    if (rows.length === 0) return;
    rows.forEach(row => {
      if (!row.quotationId) return;
      setPendingSecondary(c => c + 1);
      api.get(`/pip-related/quotation/${row.quotationId}/progress`)
        .then(res => setPipcProgress(prev => ({ ...prev, [row.quotationId]: res.data })))
        .catch(() => {})
        .finally(() => setPendingSecondary(c => c - 1));
      setPendingSecondary(c => c + 1);
      api.get(`/work-orders/${row.id}/workflow-status`)
        .then(res => setFlowByWorkOrder(prev => ({ ...prev, [row.id]: res.data })))
        .catch(() => {})
        .finally(() => setPendingSecondary(c => c - 1));
    });
    const quotationIds = [...new Set(rows.map(r => r.quotationId).filter(Boolean))].join(',');
    if (!quotationIds) return;
    setPendingSecondary(c => c + 1);
    api.get('/pip-related/summary', { params: { quotationIds } })
      .then(res => setSummaryByQuotation(res.data || {}))
      .catch(() => {})
      .finally(() => setPendingSecondary(c => c - 1));
    setPendingSecondary(c => c + 1);
    api.get('/pip-related/readiness', { params: { quotationIds } })
      .then(res => setReadinessByQuotation(res.data || {}))
      .catch(() => {})
      .finally(() => setPendingSecondary(c => c - 1));
  }, [rows]);

  useEffect(() => {
    let data = [...rows];
    if (search) {
      const t = search.toLowerCase();
      data = data.filter(r =>
        (r.companyName || '').toLowerCase().includes(t) ||
        (r.orderNumber || '').toLowerCase().includes(t)
      );
    }
    if (statusFilter !== 'ALL') data = data.filter(r => r.status === statusFilter);
    setFiltered(data);
  }, [rows, search, statusFilter]);

  // Ordenamiento de la tabla (elegido por el usuario: Nombre Comercial, Orden de Trabajo,
  // Fecha de Inspección o Fecha de Capacitación)
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const DATE_SORT_FIELDS = ['scheduledDate', 'trainingDate'];

  const handleSortRequest = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedFiltered = !sortField ? filtered : [...filtered].sort((a, b) => {
    let valueA = a[sortField];
    let valueB = b[sortField];
    if (DATE_SORT_FIELDS.includes(sortField)) {
      valueA = valueA ? new Date(valueA).getTime() : 0;
      valueB = valueB ? new Date(valueB).getTime() : 0;
    } else {
      valueA = (valueA || '').toString().toLowerCase();
      valueB = (valueB || '').toString().toLowerCase();
    }
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const load = async () => {
    try {
      setLoading(true); setError('');
      const res = await api.get('/work-orders', { params: { page: 0, size: 1000, sortBy: 'createdAt', sortDir: 'desc' } });
      const data = res.data.content || res.data || [];
      setRows(data);
    } catch (e) {
      const isNetworkError = !e.response || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED';
      setError(isNetworkError ? 'network' : 'server');
    } finally { setLoading(false); }
  };

  const openDetail = async (row) => {
    setSelected(row);
    setFieldSearch('');
    setPayments([]);
    if (row.quotationId) {
      try {
        setPaymentsLoading(true);
        const res = await api.get(`/quotations/${row.quotationId}/payments`);
        setPayments(res.data || []);
      } catch (e) {
        setPayments([]);
      } finally {
        setPaymentsLoading(false);
      }
    }
  };

  const handleGenerateConstancias = async (row) => {
    setConstanciasDialog({ open: true, loading: true, results: null, error: null, row });
    setGeneratingConstancias(true);
    try {
      const response = await api.post(`/constancias/work-order/${row.id}/generate-all`, {}, { timeout: 120000 });
      const data = response.data;
      if (data.success) {
        setConstanciasDialog(prev => ({ ...prev, loading: false, results: data }));
        loadReadiness();
      } else {
        setConstanciasDialog(prev => ({ ...prev, loading: false, error: data.message }));
      }
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      setConstanciasDialog(prev => ({
        ...prev,
        loading: false,
        error: isTimeout
          ? 'La generación tardó más de lo esperado. Verifica en Dropbox y en Servicios → Gestión Documental si los archivos ya están disponibles.'
          : (error.response?.data?.message || 'Error al generar constancias')
      }));
    } finally {
      setGeneratingConstancias(false);
    }
  };

  // Simulación visual de avance (no es real -- solo para no dejar un spinner ciego mientras
  // la petición síncrona hace su trabajo). Sube de a poco y se frena en 90% hasta que la
  // respuesta real llegue; ahí se completa a 100%.
  const startFakeProgress = (setDialogState, estimatedSeconds) => {
    const stepMs = 1500;
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += stepMs / 1000;
      const pct = Math.min(90, Math.round((elapsed / estimatedSeconds) * 90));
      setDialogState(prev => ({ ...prev, progressPct: pct }));
    }, stepMs);
    return interval;
  };

  const runGenerationJob = async (row, docType, endpoint) => {
    if (!row.quotationId) {
      setSnackbar({ open: true, message: 'Esta orden no tiene cotización vinculada', severity: 'warning' });
      return;
    }
    setGeneratingPipc(prev => new Set([...prev, row.id]));
    setPipcDialog({ open: true, loading: true, results: null, error: null, row, docType, progressPct: 0 });

    const totalEntities = readinessMap[row.id]?.totalEntities || 1;
    const estimatedSeconds = Math.max(20, totalEntities * 20);
    const progressInterval = startFakeProgress(setPipcDialog, estimatedSeconds);

    try {
      const response = await api.post(`/files/quotation/${row.quotationId}/${endpoint}`, {}, { timeout: 120000 });
      const data = response.data;
      if (data.success) {
        setPipcDialog(prev => ({ ...prev, loading: false, results: data, progressPct: 100 }));
        loadReadiness();
      } else {
        setPipcDialog(prev => ({ ...prev, loading: false, error: data.message }));
      }
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      setPipcDialog(prev => ({
        ...prev,
        loading: false,
        error: isTimeout
          ? 'La generación tardó más de lo esperado. Verifica en Dropbox y en Servicios → Gestión Documental si los archivos ya están disponibles.'
          : (error.response?.data?.message || `Error al generar archivos ${docType}`)
      }));
    } finally {
      clearInterval(progressInterval);
      setGeneratingPipc(prev => { const next = new Set(prev); next.delete(row.id); return next; });
    }
  };

  const handleGenerateRIC = (row) => runGenerationJob(row, 'RIC', 'generate-ric-entities');

  const handleGeneratePIPC = (row) => runGenerationJob(row, 'PIPC', 'generate-pipc-entities');

  // Abre el diálogo de confirmación con la lista de entes antes de disparar la generación masiva
  const openConfirmGenerate = (row, docType) => {
    setConfirmGenerateDialog({ open: true, docType, row });
    if (docType === 'CONSTANCIAS') {
      setConstanciasSummary([]);
      setConstanciasSummaryLoading(true);
      api.get(`/constancias/work-order/${row.id}/generation-summary`)
        .then(resp => setConstanciasSummary(resp.data || []))
        .catch(() => setConstanciasSummary([]))
        .finally(() => setConstanciasSummaryLoading(false));
    }
  };

  const closeConfirmGenerate = () => {
    setConfirmGenerateDialog({ open: false, docType: null, row: null });
    setConstanciasSummary([]);
  };

  const confirmAndGenerate = () => {
    const { row, docType } = confirmGenerateDialog;
    closeConfirmGenerate();
    if (docType === 'RIC') handleGenerateRIC(row);
    else if (docType === 'PIPC') handleGeneratePIPC(row);
    else if (docType === 'CONSTANCIAS') handleGenerateConstancias(row);
  };

  const READY_KEY_BY_DOC_TYPE = { RIC: 'ricReady', PIPC: 'pipcReady', CONSTANCIAS: 'constanciasReady' };

  // Un solo símbolo compacto que combina las dos señales (datos completos / ya generado) sin
  // amontonar la celda -- el detalle exacto queda en el tooltip. Si el archivo ya existe
  // (generated/hasFile) y conocemos su carpeta en Dropbox, el ícono se vuelve un link directo
  // a Gestión Documental (Centro de Gestión Documental) ya posicionado en esa carpeta.
  const renderDocStatus = (fieldsReady, generated, folderPath, hasFile = generated) => {
    let icon, tooltip;
    if (fieldsReady && generated) {
      icon = '✅'; tooltip = 'Datos completos y documento generado';
    } else if (generated) {
      icon = '📄'; tooltip = 'Documento generado, pero faltan datos por completar';
    } else if (fieldsReady) {
      icon = '📝'; tooltip = 'Datos completos, todavía no se generó el documento';
    } else {
      icon = '⏳'; tooltip = 'Faltan datos y todavía no se generó el documento';
    }

    if (hasFile && folderPath) {
      return (
        <Tooltip title={`${tooltip} — clic para abrir la carpeta en Gestión Documental`}>
          <IconButton
            size="small"
            onClick={() => navigate('/servicios/documentos', { state: { initialPath: folderPath } })}
            sx={{ p: 0.25 }}
          >
            <span>{icon}</span>
          </IconButton>
        </Tooltip>
      );
    }
    return <Tooltip title={tooltip}><span>{icon}</span></Tooltip>;
  };

  // Links útiles (mismo criterio que Programas): llevan al registro y lo abren -- cada destino ya
  // sabe abrir el que recibe en location.state.
  const goToEntity = (row, { entityType, branchId }) => {
    if (entityType === 'MATRIZ' || !branchId) {
      navigate('/clientes/empresas', { state: { highlightEntityId: row.companyId, entityType: 'MATRIZ' } });
    } else {
      navigate('/clientes/sucursales', { state: { highlightEntityId: branchId, entityType: 'SUCURSAL' } });
    }
  };
  const goToUser = (userId) => navigate('/usuarios/catalogo', { state: { highlightUserId: userId } });
  const goToAgenda = (row, ente) => navigate('/servicios/agenda', {
    state: { highlightWorkOrderId: row.id, highlightEntityType: ente.entityType, highlightBranchId: ente.branchId ?? null }
  });
  const goToEvaluation = (row, ente) => navigate('/evaluaciones/inspeccion', {
    state: { quotationId: row.quotationId, companyId: row.companyId, companyName: row.companyName,
      branchId: ente.branchId ?? null, entityType: ente.entityType }
  });
  const linkSx = { cursor: 'pointer', color: '#1565c0', '&:hover': { textDecoration: 'underline' } };
  const enteLabel = (e) => (e.entityName || '').replace(/\s*\(Matriz\)\s*$/i, '') || (e.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal');
  const progressColor = (pct) => (pct >= 100 ? '#4caf50' : pct >= 50 ? '#2196f3' : pct > 0 ? '#ff9800' : '#bdbdbd');
  const sameEnte = (a, b) => String(a.branchId ?? null) === String(b.branchId ?? null);
  // Entes de la OT: avance PIPC por ente (porEnte) + documentos por ente (readiness), unidos por ente
  const entesOf = (row) => {
    const porEnte = pipcProgress[row.quotationId]?.porEnte || [];
    const docs = readinessMap[row.id]?.entities || [];
    if (porEnte.length === 0) return docs.map((d) => ({ ...d, docs: d }));
    return porEnte.map((e) => ({ ...e, docs: docs.find((d) => sameEnte(d, e)) || null }));
  };
  // Avance por ente con las etapas del menú de Programas + recepción del cliente
  const stagesOf = (row) => {
    const summary = summaryByQuotation[row.quotationId];
    if (!summary) return [];
    return buildEnteStages({
      programStarted: !!summary.exists,
      programComplete: summary.exists ? isPipComplete(summary.pip) : false,
      entes: (summary.porEnte || []).map(e => ({ name: enteLabel(e), branchId: e.branchId ?? null, asignado: e.asignado, uipc: e.uipc, evaluacion: e.evaluacion, acompanante: e.acompanante })),
      readiness: readinessByQuotation[row.quotationId]?.entities || [],
      status: flowByWorkOrder[row.id] || null,
    });
  };
  const stageFor = (row, branchId) => stagesOf(row).find(s => String(s.branchId ?? null) === String(branchId ?? null));

  // ── Visibilidad por ente: documentos, revisión de calidad, atraso, días en etapa, vencimientos ──
  const flowEntesOf = (row) => flowByWorkOrder[row.id]?.entities || [];
  const flowEnteFor = (row, branchId) => flowEntesOf(row).find(x => String(x.branchId ?? null) === String(branchId ?? null));
  const todayLocal = new Date().toLocaleDateString('en-CA');
  // Mismo criterio que Programas: pasó su fecha estimada de fin (o la programada) sin terminar lo operativo
  const overdueDays = (e) => {
    const due = String(e?.estimatedCompletionDate || e?.scheduledDate || '').slice(0, 10);
    if (!due || due >= todayLocal || (e.percentage ?? 0) >= 100) return 0;
    return Math.round((new Date(todayLocal) - new Date(due)) / 86400000);
  };
  const daysSince = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);
  const QA_INFO = {
    PENDIENTE_REVISION: { label: 'en revisión de calidad', color: 'warning', rank: 2 },
    REVISION_1_RECHAZADA: { label: '1ª revisión rechazada', color: 'error', rank: 4 },
    REVISION_2_RECHAZADA: { label: '2ª revisión rechazada', color: 'error', rank: 4 },
    REVISION_1_APROBADA: { label: '1ª revisión aprobada', color: 'info', rank: 1 },
    REVISION_2_APROBADA: { label: 'aprobado', color: 'success', rank: 0 },
  };
  const expiringOf = (row) => flowEntesOf(row).flatMap(e => (e.expiringDocuments || []).map(d => ({ ...d, ente: e.entityName })));

  const stagesTooltip = (st) => (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{st.name}: {st.completed}/{st.total} etapas ({st.percentage}%)</Typography>
      {st.stages.map(x => (
        <Typography key={x.key} variant="caption" sx={{ display: 'block' }}>
          {x.done ? '✅' : '⏳'} {x.label}{x.orderLevel ? ' (toda la OT)' : ''}
        </Typography>
      ))}
    </Box>
  );

  // Campos filtrados por búsqueda dentro del dialog
  const dialogFields = selected
    ? buildFields(selected, payments).filter(f =>
        !fieldSearch || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) ||
        (f.section || '').toLowerCase().includes(fieldSearch.toLowerCase())
      )
    : [];

  // Agrupar por sección para el dialog
  const sections = [...new Set(dialogFields.map(f => f.section))];

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          gap: 3,
          p: 4,
        }}
      >
        {/* Ilustración SVG: cable desconectado */}
        <svg width="220" height="180" viewBox="0 0 220 180" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="40" width="78" height="100" rx="10" fill="#e3f2fd" stroke="#90caf9" strokeWidth="2"/>
          <rect x="30" y="62" width="14" height="22" rx="4" fill="#1976d2" opacity="0.35"/>
          <rect x="54" y="62" width="14" height="22" rx="4" fill="#1976d2" opacity="0.35"/>
          <rect x="39" y="94" width="20" height="16" rx="10" fill="#1976d2" opacity="0.35"/>
          <path d="M 155 90 C 155 120, 140 140, 115 158" stroke="#ff7043" strokeWidth="7" fill="none" strokeLinecap="round"/>
          <rect x="130" y="68" width="42" height="36" rx="6" fill="#ff7043"/>
          <rect x="140" y="52" width="7" height="20" rx="3" fill="#bf360c"/>
          <rect x="153" y="52" width="7" height="20" rx="3" fill="#bf360c"/>
          <text x="97" y="72" fontSize="22" fill="#ffa726">⚡</text>
          <circle cx="108" cy="90" r="4" fill="#1976d2" opacity="0.5"/>
          <circle cx="120" cy="88" r="3" fill="#1976d2" opacity="0.35"/>
          <circle cx="130" cy="85" r="2" fill="#1976d2" opacity="0.2"/>
        </svg>

        <Typography variant="h5" fontWeight="bold" color="text.primary">
          Sin conexión al servidor
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" maxWidth={380}>
          {error === 'network'
            ? 'No se pudo conectar con el servidor. Verifica que el backend esté activo e intenta de nuevo.'
            : 'El servidor respondió con un error inesperado. Intenta recargar la página.'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={load}
          sx={{ mt: 1, borderRadius: 2, px: 4 }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TableChartIcon sx={{ fontSize: 32, color: '#1976d2' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Tablero de Seguimiento</Typography>
            <Typography variant="caption" color="text.secondary">Seguimiento del cliente · Solo Administrador</Typography>
          </Box>
        </Box>
        <IconButton
          onClick={() => { load(); loadReadiness(); }}
          disabled={loading}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            boxShadow: 2,
            '&:hover': { bgcolor: 'primary.dark', transform: 'rotate(180deg)', transition: 'all 0.3s ease-in-out' },
            '&:disabled': { bgcolor: '#ccc', color: '#666' }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {pendingSecondary > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <LinearProgress sx={{ borderRadius: 1, height: 4 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Validando estatus de PIPC y documentos generados…
          </Typography>
        </Box>
      )}

      {/* ── Filters (compactos, en una sola línea) ── */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Empresa u orden…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
          sx={{ width: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Estatus</InputLabel>
          <Select value={statusFilter} label="Estatus" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="ALL">Todos</MenuItem>
            <MenuItem value="PENDIENTE">Pendiente</MenuItem>
            <MenuItem value="ASIGNADA">Asignada</MenuItem>
            <MenuItem value="PROGRAMADA">Programada</MenuItem>
            <MenuItem value="EN_PROGRESO">En Progreso</MenuItem>
            <MenuItem value="LISTA_PARA_REVISION">Lista para Revisión</MenuItem>
            <MenuItem value="COMPLETADA">Completada</MenuItem>
            <MenuItem value="CANCELADA">Cancelada</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          {filtered.length} registro{filtered.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* ── Table ── */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <Paper elevation={2}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 230px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { label: ' ', key: 'expand' },
                    { label: 'Nombre Comercial', field: 'companyName' },
                    { label: 'Cotización' },
                    { label: 'Orden de Trabajo', field: 'orderNumber' },
                    { label: 'Ejecutivo' },
                    { label: 'F. Insp. Programada', field: 'scheduledDate' },
                    { label: 'F. Capacitación', field: 'trainingDate' },
                    { label: 'Estatus Seguimiento' },
                    { label: 'Factura Municipio' },
                    { label: 'Evidencia PC' },
                    { label: 'F. Entrega Final' },
                    { label: 'Encuesta' },
                    { label: 'PIPC enviado' },
                    { label: 'Avance por ente' },
                    { label: 'Documentos' },
                    { label: '' }
                  ].map(({ label, field, key }) => (
                    <TableCell key={key || label} sx={{ fontWeight: 700, backgroundColor: '#1565c0', color: '#fff', whiteSpace: 'nowrap', py: 1, ...(key === 'expand' && { width: 40, px: 0.5 }) }}>
                      {field ? (
                        <TableSortLabel
                          active={sortField === field}
                          direction={sortField === field ? sortDirection : 'asc'}
                          onClick={() => handleSortRequest(field)}
                          sx={{
                            color: '#fff !important',
                            '& .MuiTableSortLabel-icon': { color: '#fff !important' }
                          }}
                        >
                          {label}
                        </TableSortLabel>
                      ) : label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFiltered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={16} align="center" sx={{ py: 6, color: '#999' }}>No hay órdenes de trabajo</TableCell>
                  </TableRow>
                ) : sortedFiltered.map((row, idx) => (
                  <React.Fragment key={row.id}>
                  <TableRow hover sx={{
                    backgroundColor: (readinessMap[row.id]?.ricReady || readinessMap[row.id]?.pipcReady) ? '#f1f8e9' : idx % 2 === 0 ? '#fafafa' : '#fff',
                    ...(readinessMap[row.id]?.ricReady || readinessMap[row.id]?.pipcReady) && { borderLeft: '3px solid #2e7d32' },
                  }}>
                    <TableCell sx={{ px: 0.5 }}>
                      <Tooltip title={expandedRows.has(row.id) ? 'Ocultar entes' : `Ver entes (${entesOf(row).length || '…'})`}>
                        <IconButton size="small" onClick={() => toggleRowExpanded(row.id)}
                          sx={{ bgcolor: expandedRows.has(row.id) ? '#1565c0' : '#e3f2fd', color: expandedRows.has(row.id) ? '#fff' : '#1565c0',
                            '&:hover': { bgcolor: expandedRows.has(row.id) ? '#0d47a1' : '#bbdefb' } }}>
                          {expandedRows.has(row.id) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {row.companyId ? (
                        <Tooltip title="Abrir la empresa">
                          <Typography variant="body2" component="span" sx={{ fontWeight: 600, ...linkSx }}
                            onClick={() => goToEntity(row, { entityType: 'MATRIZ' })}>
                            {row.companyName || '—'}
                          </Typography>
                        </Tooltip>
                      ) : (row.companyName || '—')}
                      {(() => {
                        const docs = expiringOf(row);
                        if (docs.length === 0) return null;
                        const expired = docs.filter(d => getExpirationStatus(d.expirationDate).isExpired).length;
                        const soon = docs.length - expired;
                        const summary = [expired ? `${expired} vencido${expired > 1 ? 's' : ''}` : null,
                          soon ? `${soon} por vencer (30 días)` : null].filter(Boolean).join(' · ');
                        return (
                          <Tooltip title={(
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Documentos del cliente: {summary}</Typography>
                              {docs.map((d, i) => (
                                <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                                  {(d.documentType || '').replace(/_/g, ' ').toLowerCase()} · {d.ente} · {fmtDate(d.expirationDate)}
                                </Typography>
                              ))}
                            </Box>
                          )}>
                            <WarningIcon sx={{ fontSize: 16, ml: 0.75, verticalAlign: 'middle', color: expired ? '#c62828' : '#ef6c00' }} />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.quotationId ? (
                        <Tooltip title="Abrir cotización">
                          <Typography
                            variant="body2"
                            onClick={() => navigate(`/cotizaciones/new-quotation?edit=${row.quotationId}`)}
                            sx={{
                              color: '#1565c0', cursor: 'pointer', fontWeight: 600,
                              textDecoration: 'underline', '&:hover': { color: '#0d47a1' }
                            }}
                          >
                            {row.quotationFolio || `COT-${row.quotationId}`}
                          </Typography>
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {row.id ? (
                        <Tooltip title="Abrir orden de trabajo">
                          <Chip
                            label={row.orderNumber || '—'}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => navigate('/servicios/ordenes-trabajo', { state: { highlightWorkOrderId: row.id } })}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip label={row.orderNumber || '—'} size="small" color="primary" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        const names = [...new Set((pipcProgress[row.quotationId]?.porEnte || []).map(e => e.assignedUserName).filter(Boolean))];
                        if (names.length === 0) return <Typography variant="caption" sx={{ color: '#bbb', fontStyle: 'italic' }}>Sin asignar</Typography>;
                        return names.map(n => <Typography key={n} variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{n}</Typography>);
                      })()}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.scheduledDate)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.trainingDate ? (
                        <Box>
                          <Typography variant="body2">{fmtDate(row.trainingDate)}</Typography>
                          {/* earliestTrainingDate/latestTrainingDate: rollup de todos los programas
                              de todos los entes de la orden -- si difieren, hay más de una fecha
                              distinta entre programas y no basta con mostrar solo la más próxima. */}
                          {row.latestTrainingDate && row.latestTrainingDate !== row.earliestTrainingDate && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              → {fmtDate(row.latestTrainingDate)}
                            </Typography>
                          )}
                        </Box>
                      ) : <Typography variant="body2" sx={{ color: '#bbb', fontStyle: 'italic', fontSize: '0.75rem' }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABEL[row.status] || 'Pendiente'} size="small" color={STATUS_COLOR[row.status] || 'default'} />
                    </TableCell>
                    <TableCell>
                      {row.requiresInvoice != null
                        ? <Chip label={row.requiresInvoice ? 'Sí' : 'No'} size="small" color={row.requiresInvoice ? 'success' : 'default'} />
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={CP_LABEL[row.civilProtectionApproval] || '—'} size="small"
                        color={row.civilProtectionApproval === 'si' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.completedAt)}</TableCell>
                    <TableCell sx={{ color: '#bbb', fontStyle: 'italic', fontSize: '0.75rem' }}>—</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {flowByWorkOrder[row.id]?.pipcSent?.at ? (
                        <Tooltip title={flowByWorkOrder[row.id].pipcSent.description || ''}>
                          <Chip size="small" color="success" variant="outlined" label={fmtDate(flowByWorkOrder[row.id].pipcSent.at)} sx={{ height: 20, fontSize: '0.68rem' }} />
                        </Tooltip>
                      ) : <Typography variant="caption" sx={{ color: '#bbb', fontStyle: 'italic' }}>No enviado</Typography>}
                    </TableCell>
                    <TableCell sx={{ minWidth: 240, pr: 1 }}>
                      {(() => {
                        const entes = pipcProgress[row.quotationId]?.porEnte || [];
                        if (!pipcProgress[row.quotationId]) return <Typography variant="caption" color="text.secondary">—</Typography>;
                        if (entes.length === 0) return <Typography variant="caption" color="text.secondary">Sin entes</Typography>;
                        const visible = entes.slice(0, 3);
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, cursor: 'pointer' }}
                            onClick={() => { setPipcDialogRow(row); setPipcDialogOpen(true); }}>
                            {visible.map((e) => {
                              const st = stageFor(row, e.branchId);
                              const pct = st ? st.percentage : 0;
                              return (
                                <React.Fragment key={e.workOrderDetailId}>
                                <Tooltip title={st ? stagesTooltip(st) : 'Calculando…'}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography variant="caption" noWrap sx={{ width: 90, fontWeight: 600 }}>{enteLabel(e)}</Typography>
                                    <LinearProgress variant="determinate" value={pct}
                                      sx={{ flexGrow: 1, minWidth: 50, height: 5, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: progressColor(pct), borderRadius: 3 } }} />
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: progressColor(pct), minWidth: 34, textAlign: 'right' }}>
                                      {st ? `${st.completed}/${st.total}` : '…'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                                {(() => {
                                  const late = overdueDays(e);
                                  const current = st?.stages?.find(x => !x.done);
                                  const days = daysSince(flowEnteFor(row, e.branchId)?.lastActivityAt);
                                  if (!late && !current) return null;
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: '96px', mt: -0.3 }}>
                                      {late > 0 && (
                                        <Tooltip title={`Fecha estimada de cierre: ${fmtDate(e.estimatedCompletionDate || e.scheduledDate)}`}>
                                          <Chip size="small" color="error" label={`Atrasado ${late} d`} sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }} />
                                        </Tooltip>
                                      )}
                                      {current && (
                                        <Tooltip title="Etapa actual · días desde el último movimiento del ente">
                                          <Typography variant="caption" noWrap sx={{ fontSize: '0.65rem', color: days >= 7 ? '#c62828' : 'text.secondary' }}>
                                            {current.label}{days != null ? ` · ${days} d` : ''}
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  );
                                })()}
                                </React.Fragment>
                              );
                            })}
                            {entes.length > visible.length && (
                              <Typography variant="caption" sx={linkSx}
                                onClick={(ev) => { ev.stopPropagation(); if (!expandedRows.has(row.id)) toggleRowExpanded(row.id); }}>
                                +{entes.length - visible.length} ente{entes.length - visible.length > 1 ? 's' : ''} más
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ minWidth: 260 }}>
                      {(() => {
                        const r = readinessMap[row.id];
                        const flowEntes = flowEntesOf(row);
                        const total = flowEntes.length || r?.totalEntities || 1;
                        // Todos cuentan GENERADOS; el color del chip es el peor estado de revisión de calidad
                        const docChip = (label, key, readyCount) => {
                          const docs = flowEntes.map(e => ({ ente: e.entityName, doc: e.documents?.[key] }));
                          const generated = docs.filter(x => x.doc).length;
                          const worst = docs.filter(x => x.doc).map(x => QA_INFO[x.doc.qaStatus])
                            .filter(Boolean).sort((a, b) => b.rank - a.rank)[0];
                          const ready = (readyCount ?? 0) > generated;
                          const color = generated === 0 ? 'default' : (worst?.color || 'default');
                          return (
                            <Tooltip key={label} title={(
                              <Box>
                                {docs.map((x, i) => (
                                  <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                                    {x.ente}: {x.doc ? `generado ${fmtDate(x.doc.generatedAt)} · ${QA_INFO[x.doc.qaStatus]?.label || 'sin revisión'}` : 'sin generar'}
                                  </Typography>
                                ))}
                                {ready && <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>● Listo para generar</Typography>}
                              </Box>
                            )}>
                              <Chip
                                size="small"
                                label={`${label} ${generated}/${total}${ready ? ' ●' : ''}`}
                                color={color}
                                variant={generated === total ? 'filled' : 'outlined'}
                                sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}
                              />
                            </Tooltip>
                          );
                        };
                        const cons = flowByWorkOrder[row.id]?.constancias;
                        const consReady = (r?.constanciasReadyCount ?? 0) > 0 && (cons?.generated ?? 0) < (cons?.expected ?? 0);
                        return (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {docChip('RIC', 'REPORTE_INSPECCION', r?.ricReadyCount)}
                            {docChip('PIPC', 'PIPC', r?.pipcReadyCount)}
                            {docChip('Acta', 'ACTA_CONSTITUTIVA_UIPC')}
                            {docChip('Carta', 'CARTA_CORRESPONSABILIDAD')}
                            {(() => {
                              // Mismo color que los demás: peor estado de revisión de calidad de los
                              // archivos combinados de constancias (uno por ente y curso)
                              const qaDocs = flowEntes.flatMap(e => (e.constanciasQa || []).map(doc => ({ ente: e.entityName, doc })));
                              const worst = qaDocs.map(x => QA_INFO[x.doc.qaStatus]).filter(Boolean).sort((a, b) => b.rank - a.rank)[0];
                              const complete = !!cons?.expected && cons.generated === cons.expected;
                              const color = (cons?.generated ?? 0) === 0 ? 'default' : (worst?.color || (complete ? 'secondary' : 'default'));
                              return (
                                <Tooltip title={(
                                  <Box>
                                    <Typography variant="caption" sx={{ display: 'block' }}>Constancias generadas: {cons?.generated ?? 0} de {cons?.expected ?? 0}</Typography>
                                    {qaDocs.map((x, i) => (
                                      <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                                        {x.ente} · {x.doc.label || 'Constancias'}: {QA_INFO[x.doc.qaStatus]?.label || 'sin revisión'}
                                      </Typography>
                                    ))}
                                    {consReady && <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>● Listas para generar</Typography>}
                                  </Box>
                                )}>
                                  <Chip
                                    size="small"
                                    label={`Const. ${cons?.generated ?? 0}/${cons?.expected ?? 0}${consReady ? ' ●' : ''}`}
                                    color={color}
                                    variant={complete ? 'filled' : 'outlined'}
                                    sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}
                                  />
                                </Tooltip>
                              );
                            })()}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ pr: 1 }}>
                      <Tooltip title={(readinessMap[row.id]?.ricReady || readinessMap[row.id]?.pipcReady) ? 'Documentos listos para generar' : ''}>
                        <IconButton
                          size="small"
                          onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(row); }}
                        >
                          <MoreVertIcon fontSize="small" sx={(readinessMap[row.id]?.ricReady || readinessMap[row.id]?.pipcReady) ? { color: '#2e7d32' } : {}} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {expandedRows.has(row.id) && (
                    <TableRow>
                      <TableCell colSpan={16} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse in={expandedRows.has(row.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 1, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            {entesOf(row).length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                {pendingSecondary > 0 ? 'Cargando entes…' : 'Esta orden no tiene entes programados.'}
                              </Typography>
                            ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {['Ente', 'Ejecutivo Asignado', 'Programado', 'Avance por etapas', 'Evaluación', 'RIC', 'PIPC', 'Constancias', 'Capacitación'].map((h, i) => (
                                    <TableCell key={h} sx={{ fontWeight: 700, border: 0 }} align={i >= 4 ? 'center' : 'left'}>{h}</TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {entesOf(row).map((ent, i) => {
                                  const d = ent.docs;
                                  const st = stageFor(row, ent.branchId);
                                  const pct = st ? st.percentage : null;
                                  const evalDone = (ent.items || []).find((it) => it.key === 'evaluacion_inspeccion')?.completed;
                                  const userName = ent.assignedUserName || d?.assignedUserName;
                                  return (
                                  <TableRow key={ent.workOrderDetailId || i}>
                                    <TableCell sx={{ border: 0 }}>
                                      <Tooltip title={ent.entityType === 'MATRIZ' || !ent.branchId ? 'Abrir la empresa (matriz)' : 'Abrir la sucursal'}>
                                        <Typography variant="body2" component="span" sx={{ fontWeight: 600, ...linkSx }}
                                          onClick={() => goToEntity(row, ent)}>
                                          {enteLabel(ent)}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ border: 0 }}>
                                      {ent.assignedUserId ? (
                                        <Tooltip title="Abrir el usuario">
                                          <Typography variant="body2" component="span" sx={linkSx} onClick={() => goToUser(ent.assignedUserId)}>
                                            {userName || `Usuario ${ent.assignedUserId}`}
                                          </Typography>
                                        </Tooltip>
                                      ) : (
                                        <Typography variant="body2" color={userName ? 'text.primary' : 'text.secondary'}>{userName || 'Sin asignar'}</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ border: 0, whiteSpace: 'nowrap' }}>
                                      <Tooltip title="Abrir la programación de este ente en la Agenda">
                                        <Typography variant="body2" component="span" sx={linkSx} onClick={() => goToAgenda(row, ent)}>
                                          {ent.scheduledDate ? fmtDate(ent.scheduledDate) : 'Programar'}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ border: 0, minWidth: 140 }}>
                                      {pct != null ? (
                                        <Tooltip title={stagesTooltip(st)}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress variant="determinate" value={pct}
                                              sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: progressColor(pct) } }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{st.completed}/{st.total}</Typography>
                                          </Box>
                                        </Tooltip>
                                      ) : '—'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ border: 0 }}>
                                      <Tooltip title="Abrir la evaluación de inspección de este ente">
                                        <IconButton size="small" onClick={() => goToEvaluation(row, ent)} sx={{ p: 0.25 }}>
                                          <span>{evalDone ? '✅' : '⏳'}</span>
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell align="center" sx={{ border: 0 }}>{d ? renderDocStatus(d.ricReady, d.ricGenerated, d.reportsFolderPath) : '—'}</TableCell>
                                    <TableCell align="center" sx={{ border: 0 }}>{d ? renderDocStatus(d.pipcReady, d.pipcGenerated, d.reportsFolderPath) : '—'}</TableCell>
                                    <TableCell align="center" sx={{ border: 0 }}>
                                      {d ? renderDocStatus(d.constanciasReady, d.constanciasReady, d.constanciasFolderPath, (d.constanciasVerifiedCount || 0) > 0) : '—'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ border: 0 }}>
                                      {d ? (
                                        <Tooltip title={
                                          !d.hasTraining ? 'Sin capacitación programada'
                                            : d.trainingCompleted ? `Impartida el ${fmtDate(d.trainingDate)}`
                                            : `Programada para el ${fmtDate(d.trainingDate)}`
                                        }>
                                          <span>{!d.hasTraining ? '—' : d.trainingCompleted ? '✅' : '⏳'}</span>
                                        </Tooltip>
                                      ) : '—'}
                                    </TableCell>
                                  </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Row Actions Menu ── */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuRow(null); }}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 2, boxShadow: 4 } }}
      >
        <MenuItem onClick={() => { openDetail(menuRow); setMenuAnchor(null); setMenuRow(null); }}>
          <ListItemIcon><VisibilityIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>Ver Detalle</ListItemText>
        </MenuItem>
        <Divider />
        {menuRow?.quotationId && (
          <MenuItem onClick={() => { openConfirmGenerate(menuRow, 'RIC'); setMenuAnchor(null); setMenuRow(null); }}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" sx={{ color: readinessMap[menuRow?.id]?.ricReady ? '#2e7d32' : '#9e9e9e' }} />
            </ListItemIcon>
            <ListItemText>
              Generar RIC{readinessMap[menuRow?.id]?.totalEntities ? ` (${readinessMap[menuRow.id].totalEntities} entes)` : ''}
            </ListItemText>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              {readinessMap[menuRow?.id]?.ricReady && <Chip label="Completo" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />}
              {readinessMap[menuRow?.id]?.ricGenerated && <Chip label="Generado" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
            </Box>
          </MenuItem>
        )}
        {menuRow?.quotationId && (
          <MenuItem
            onClick={() => { openConfirmGenerate(menuRow, 'PIPC'); setMenuAnchor(null); setMenuRow(null); }}
            disabled={generatingPipc.has(menuRow?.id)}
          >
            <ListItemIcon>
              {generatingPipc.has(menuRow?.id) ? <CircularProgress size={16} /> : <ArticleIcon fontSize="small" sx={{ color: readinessMap[menuRow?.id]?.pipcReady ? '#2e7d32' : '#9e9e9e' }} />}
            </ListItemIcon>
            <ListItemText>
              Generar PIPC{readinessMap[menuRow?.id]?.totalEntities ? ` (${readinessMap[menuRow.id].totalEntities} entes)` : ''}
            </ListItemText>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              {readinessMap[menuRow?.id]?.pipcReady && <Chip label="Completo" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />}
              {readinessMap[menuRow?.id]?.pipcGenerated && <Chip label="Generado" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
            </Box>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { openConfirmGenerate(menuRow, 'CONSTANCIAS'); setMenuAnchor(null); setMenuRow(null); }}
          disabled={generatingConstancias}
        >
          <ListItemIcon>
            {generatingConstancias ? <CircularProgress size={16} /> : <GroupsIcon fontSize="small" sx={{ color: readinessMap[menuRow?.id]?.constanciasReady ? '#2e7d32' : '#9e9e9e' }} />}
          </ListItemIcon>
          <ListItemText>
            Generar Constancias{readinessMap[menuRow?.id]?.totalEntities ? ` (${readinessMap[menuRow.id].totalEntities} entes)` : ''}
          </ListItemText>
          {readinessMap[menuRow?.id]?.constanciasReady && <Chip label="Listo" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem', ml: 1 }} />}
          {(() => {
            const extraviadas = (readinessMap[menuRow?.id]?.constanciasIssues || [])
              .filter(i => i.reason === 'Archivo extraviado en Dropbox');
            if (extraviadas.length === 0) return null;
            return (
              <Tooltip
                title={extraviadas.map(i => `${i.workerName} (${i.entityName})`).join(', ')}
                placement="right"
              >
                <Chip
                  label={`⚠ ${extraviadas.length} extraviada${extraviadas.length !== 1 ? 's' : ''}`}
                  size="small"
                  color="warning"
                  sx={{ height: 18, fontSize: '0.6rem', ml: 1 }}
                />
              </Tooltip>
            );
          })()}
        </MenuItem>
      </Menu>

      {/* ── Confirmación antes de generar (aclara que aplica a todos los entes) ── */}
      <Dialog
        open={confirmGenerateDialog.open}
        onClose={closeConfirmGenerate}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmGenerateDialog.docType === 'CONSTANCIAS' ? 'Generar Constancias' : `Generar ${confirmGenerateDialog.docType} — todos los entes`}
        </DialogTitle>
        <DialogContent>
          {confirmGenerateDialog.docType === 'CONSTANCIAS' ? (
            constanciasSummaryLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Calculando qué entes recibirán constancias...</Typography>
              </Box>
            ) : constanciasSummary.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Esta orden de trabajo no tiene entes registrados.
              </Typography>
            ) : (() => {
              const includedItems = constanciasSummary.filter(i => i.included);
              const excludedItems = constanciasSummary.filter(i => !i.included);
              return (
                <>
                  {includedItems.length > 0 ? (
                    <>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Se generarán constancias para:
                      </Typography>
                      {includedItems.map((item, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75,
                            borderBottom: idx < includedItems.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}
                        >
                          {item.hasTraining
                            ? <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20, mt: 0.2 }} />
                            : <WarningIcon sx={{ color: '#ed6c02', fontSize: 20, mt: 0.2 }} />}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {item.entityLabel} — {item.memberCount} integrante{item.memberCount === 1 ? '' : 's'}
                            </Typography>
                            {!item.hasTraining && (
                              <Typography variant="caption" color="#ed6c02">
                                Sin capacitación registrada
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Typography variant="body2" color="#ed6c02" fontWeight={600} sx={{ mb: 1.5 }}>
                      No se generará ninguna constancia — ver los motivos abajo.
                    </Typography>
                  )}

                  {excludedItems.length > 0 && (
                    <Box sx={{ mt: includedItems.length > 0 ? 2 : 0 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        No se generarán para:
                      </Typography>
                      {excludedItems.map((item, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75,
                            borderBottom: idx < excludedItems.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}
                        >
                          <RemoveCircleOutlineIcon sx={{ color: '#9e9e9e', fontSize: 20, mt: 0.2 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary">
                              {item.entityLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.reason}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {includedItems.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <FolderIcon sx={{ color: '#757575', fontSize: 18, mt: 0.2 }} />
                      <Typography variant="caption" color="text.secondary">
                        Podrás consultar las constancias generadas en Servicios &gt; Gestión Documental.
                      </Typography>
                    </Box>
                  )}
                </>
              );
            })()
          ) : (() => {
            const row = confirmGenerateDialog.row;
            const readiness = row ? readinessMap[row.id] : null;
            const readyKey = READY_KEY_BY_DOC_TYPE[confirmGenerateDialog.docType];
            const generatedKey = confirmGenerateDialog.docType === 'RIC' ? 'ricGenerated'
              : confirmGenerateDialog.docType === 'PIPC' ? 'pipcGenerated' : null;
            const entities = readiness?.entities || [];
            return (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Esto va a generar un documento {confirmGenerateDialog.docType} para la matriz y cada
                  sucursal vinculada a esta orden de trabajo{entities.length > 0 ? ` (${entities.length} en total)` : ''}.
                  Si ya existía un archivo para alguno de estos entes, se va a reemplazar.
                </Typography>
                {entities.length > 0 && (
                  <List dense sx={{ backgroundColor: '#fafafa', borderRadius: 1 }}>
                    {entities.map((ent, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={ent.entityName} />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {ent[readyKey]
                            ? <Chip label="Completo" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                            : <Chip label="Faltan datos" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />}
                          {generatedKey && ent[generatedKey] && (
                            <Chip label="Generado" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmGenerate}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={confirmAndGenerate}
            disabled={confirmGenerateDialog.docType === 'CONSTANCIAS' && (constanciasSummaryLoading || !constanciasSummary.some(i => i.included))}
          >
            Confirmar y generar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Detail Dialog ── */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>

        {/* Encabezado del dialog */}
        <Box sx={{ background: 'linear-gradient(135deg, #1565c0, #1976d2)', color: '#fff', px: 3, pt: 2, pb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{selected?.orderNumber}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>{selected?.companyName}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setSelected(null)} sx={{ color: '#fff', mt: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {/* Búsqueda de campos dentro del dialog */}
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar campo…"
            value={fieldSearch}
            onChange={e => setFieldSearch(e.target.value)}
            autoComplete="off"
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} /></InputAdornment>,
              sx: { color: '#fff', '& input': { color: '#fff', '&::placeholder': { color: 'rgba(255,255,255,0.6)' } }, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 1, mt: 1.5 },
            }}
            sx={{ mt: 1.5, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' } }}
          />
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {selected && (
            sections.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: '#999' }}>
                <SearchIcon sx={{ fontSize: 36, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">Sin resultados para "{fieldSearch}"</Typography>
              </Box>
            ) : sections.map((sec, si) => {
              const fields = dialogFields.filter(f => f.section === sec);
              return (
                <Box key={sec}>
                  {/* Encabezado de sección */}
                  <Box sx={{ px: 2.5, py: 0.75, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: SECTION_COLOR[sec] }} />
                    <Typography variant="caption" fontWeight={700} sx={{ color: SECTION_COLOR[sec], textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      {sec}
                    </Typography>
                    <Chip label={fields.length} size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 'auto' }} />
                  </Box>

                  {/* Lista de campos */}
                  <List dense disablePadding>
                    {fields.map((f, fi) => (
                      <ListItem key={fi} divider sx={{ px: 2.5, py: 0.75, '&:last-child': { borderBottom: 'none' } }}>
                        <ListItemText
                          primary={
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              {f.label}
                            </Typography>
                          }
                          secondary={null}
                        />
                        <ListItemSecondaryAction>
                          {f.pending
                            ? <Chip label="Pendiente" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', color: '#bbb', borderColor: '#e0e0e0' }} />
                            : <Typography variant="body2" fontWeight={f.bold ? 700 : 500}
                                sx={{ textAlign: 'right', maxWidth: 220, color: f.highlight ? '#1b5e20' : f.bold ? '#b71c1c' : 'inherit' }}>
                                {f.value || '—'}
                              </Typography>
                          }
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  {si < sections.length - 1 && <Divider />}
                </Box>
              );
            })
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {paymentsLoading ? 'Cargando abonos…' : `${dialogFields.length} campos · ${payments.length} abono${payments.length !== 1 ? 's' : ''}${fieldSearch ? ` · filtrado por "${fieldSearch}"` : ''}`}
          </Typography>
          <Button size="small" onClick={() => setSelected(null)} variant="outlined">Cerrar</Button>
        </DialogActions>
      </Dialog>
      <PipcProgressDialog
        open={pipcDialogOpen}
        onClose={() => setPipcDialogOpen(false)}
        quotationId={pipcDialogRow?.quotationId}
        companyName={pipcDialogRow?.companyName}
      />

      {/* ── Dialog generación Constancias ── */}
      <Dialog
        open={constanciasDialog.open}
        onClose={() => !constanciasDialog.loading && setConstanciasDialog(prev => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          {constanciasDialog.loading ? 'Generando constancias UIPC…' : constanciasDialog.error ? '❌ Error al generar constancias' : '✅ Constancias generadas'}
        </DialogTitle>
        <DialogContent>
          {constanciasDialog.loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" textAlign="center" color="text.secondary">
                Generando y subiendo las constancias UIPC a Dropbox para cada miembro registrado.
              </Typography>
              <Typography variant="caption" textAlign="center" color="text.secondary">
                Esto puede tomar unos segundos…
              </Typography>
            </Box>
          )}
          {!constanciasDialog.loading && constanciasDialog.results && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {constanciasDialog.results.count === 0 && constanciasDialog.results.skippedCount > 0 ? (
                <Alert severity="info">
                  No se generó ninguna constancia nueva: {constanciasDialog.results.skippedCount} ya estaba{constanciasDialog.results.skippedCount !== 1 ? 'n' : ''} generada{constanciasDialog.results.skippedCount !== 1 ? 's' : ''} previamente.
                </Alert>
              ) : (
                <>
                  <Alert severity="success">
                    {constanciasDialog.results.count} constancia{constanciasDialog.results.count !== 1 ? 's' : ''} generada{constanciasDialog.results.count !== 1 ? 's' : ''} y subida{constanciasDialog.results.count !== 1 ? 's' : ''} a Dropbox exitosamente.
                    {constanciasDialog.results.skippedCount > 0 && ` (${constanciasDialog.results.skippedCount} ya existía${constanciasDialog.results.skippedCount !== 1 ? 'n' : ''} y se omitió/omitieron).`}
                  </Alert>
                  <Alert severity="info">
                    Los archivos quedan disponibles en <strong>Servicios → Gestión Documental</strong> bajo la carpeta <strong>Documentos/Constancias</strong> de la empresa.
                  </Alert>
                </>
              )}
            </Box>
          )}
          {!constanciasDialog.loading && constanciasDialog.error && (
            <Alert severity="error">{constanciasDialog.error}</Alert>
          )}
        </DialogContent>
        {!constanciasDialog.loading && (
          <DialogActions>
            <Button onClick={() => setConstanciasDialog(prev => ({ ...prev, open: false }))} variant="contained" size="small">
              Cerrar
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ── Dialog generación PIPC ── */}
      <Dialog
        open={pipcDialog.open}
        onClose={() => !pipcDialog.loading && setPipcDialog(prev => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          {pipcDialog.loading ? `Generando archivos ${pipcDialog.docType}…` : pipcDialog.error ? `❌ Error al generar ${pipcDialog.docType}` : `✅ Archivos ${pipcDialog.docType} generados`}
        </DialogTitle>
        <DialogContent>
          {pipcDialog.loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2, width: '100%' }}>
              <Typography variant="body2" textAlign="center" color="text.secondary">
                Generando y subiendo los archivos {pipcDialog.docType} a Dropbox por cada ente de la cotización.
              </Typography>
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={pipcDialog.progressPct}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Typography variant="caption" textAlign="center" color="text.secondary">
                Esto puede tomar unos minutos…
              </Typography>
            </Box>
          )}
          {!pipcDialog.loading && pipcDialog.results && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {pipcDialog.results.total === 0 ? (
                <Alert severity="warning">No se encontraron entes registrados en la cotización.</Alert>
              ) : (
                <>
                  <Alert severity={pipcDialog.results.errorCount > 0 ? 'warning' : 'success'}>
                    {pipcDialog.results.successCount} de {pipcDialog.results.total} archivo{pipcDialog.results.total !== 1 ? 's' : ''} {pipcDialog.docType} subido{pipcDialog.results.total !== 1 ? 's' : ''} a Dropbox correctamente.
                    {pipcDialog.results.errorCount > 0 && ` ${pipcDialog.results.errorCount} con error.`}
                  </Alert>
                  {pipcDialog.results.results?.map((r, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: r.success ? '#2e7d32' : '#c62828' }}>
                        {r.success ? '✅' : '❌'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{r.entityName}</Typography>
                    </Box>
                  ))}
                </>
              )}
              {pipcDialog.results.successCount > 0 && (
                <Alert severity="info" sx={{ mt: 0.5 }}>
                  Los archivos quedan disponibles en <strong>Servicios → Gestión Documental</strong> de la orden de trabajo correspondiente.
                </Alert>
              )}
            </Box>
          )}
          {!pipcDialog.loading && pipcDialog.error && (
            <Alert severity="error">{pipcDialog.error}</Alert>
          )}
        </DialogContent>
        {!pipcDialog.loading && (
          <DialogActions>
            <Button onClick={() => setPipcDialog(prev => ({ ...prev, open: false }))} variant="contained" size="small">
              Cerrar
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

